import { z } from "zod";
import { defineRoute } from "../http/route.js";
/**
 * Centrifugo's questions to the platform (ADR 0029, card 145), on the private `internal` surface: who a connection is,
 * and whether it may go on. Centrifugo presents the proxies' secret in the `X-Realtime-Secret` header.
 */
const ConnectionMeta = z
    .object({ userId: z.string(), tenantId: z.string(), membershipId: z.string(), deviceId: z.string() })
    .describe("Server-side only: who the connection is, for the refresh proxy's checks");
/** A connection the platform ends: 4000–4499 advises the client to reconnect, 4500–4999 not. */
const Disconnect = z.object({
    disconnect: z.object({ code: z.number().int().min(4000).max(4999), reason: z.string().max(32) }),
});
export const RealtimeConnectAnswer = z.union([
    z.object({
        result: z.object({
            user: z.string().describe("The membership, whose personal channel the connection is subscribed to"),
            expire_at: z.number().int().describe("Unix seconds; Centrifugo asks the refresh proxy before then"),
            meta: ConnectionMeta,
        }),
    }),
    Disconnect,
]);
export const realtimeConnect = defineRoute({
    id: "internal.realtime.connect",
    method: "POST",
    path: "/internal/realtime/connect",
    surface: "internal",
    auth: "signature",
    permission: "none",
    marks: [],
    summary: "Centrifugo asks who a new connection is: the desktop's platform token, sent as the connection's data",
    prd: ["IM-1"],
    request: {
        body: z.looseObject({
            client: z.string(),
            transport: z.string(),
            data: z.looseObject({ token: z.string().max(4096).optional() }).optional(),
        }),
    },
    responses: {
        200: { description: "The connection's identity, or why it ends", schema: RealtimeConnectAnswer },
    },
    errors: ["NOT_FOUND", "VALIDATION_FAILED"],
});
export const RealtimeRefreshAnswer = z.object({
    result: z.object({ expired: z.boolean(), expire_at: z.number().int().optional() }),
});
export const realtimeRefresh = defineRoute({
    id: "internal.realtime.refresh",
    method: "POST",
    path: "/internal/realtime/refresh",
    surface: "internal",
    auth: "signature",
    permission: "none",
    marks: [],
    summary: "Centrifugo asks whether a connection may go on: its device and membership are checked again",
    prd: ["IM-1"],
    request: {
        body: z.looseObject({ client: z.string(), user: z.string(), meta: ConnectionMeta.partial().optional() }),
    },
    responses: { 200: { description: "Extended, or expired", schema: RealtimeRefreshAnswer } },
    errors: ["NOT_FOUND", "VALIDATION_FAILED"],
});
export const realtimeRoutes = [realtimeConnect, realtimeRefresh];
//# sourceMappingURL=realtime.routes.js.map